import React, { createContext, useContext, useState, useCallback } from "react";
import { TaskInterface } from "@/interfaces";
import { StorageUtils } from "@/utils/flow/flowUtils";

// ==================== TYPES ====================
interface TaskContextValue {
  extractedTasks: TaskInterface[];
  selectedTasks: Set<string>;
  isExtracting: boolean;
  isGenerating: boolean;
  extractionError: string | null;

  // Task operations
  setExtractedTasks: (tasks: TaskInterface[]) => void;
  selectTask: (taskId: string, selected: boolean) => void;
  selectAllTasks: (selected: boolean) => void;
  addCustomTask: (task: TaskInterface) => void;
  removeTask: (taskId: string) => void;
  clearExtractedTasks: () => void;

  // Extraction state
  setIsExtracting: (isExtracting: boolean) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setExtractionError: (error: string | null) => void;

  // Helpers
  getSelectedTasksList: () => TaskInterface[];
}

// ==================== CONTEXT ====================
const TaskContext = createContext<TaskContextValue | undefined>(undefined);

// ==================== PROVIDER ====================
export const TaskContextProvider: React.FC<{
  children: React.ReactNode;
  initialTasks?: TaskInterface[];
}> = ({ children, initialTasks = [] }) => {
  const [extractedTasks, setExtractedTasksState] =
    useState<TaskInterface[]>(initialTasks);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(() => {
    return new Set(initialTasks.map((task) => task.taskInterfaceID));
  });
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  // ==================== TASK OPERATIONS ====================
  const setExtractedTasks = useCallback((tasks: TaskInterface[]) => {
    setExtractedTasksState(tasks);
    StorageUtils.saveExtractedTasks(tasks);
  }, []);

  const selectTask = useCallback((taskId: string, selected: boolean) => {
    setSelectedTasks((prev) => {
      const newSet = new Set<string>(prev);
      if (selected) {
        newSet.add(taskId);
      } else {
        newSet.delete(taskId);
      }
      StorageUtils.saveSelectedTasks(newSet);
      return newSet;
    });
  }, []);

  const selectAllTasks = useCallback(
    (selected: boolean) => {
      const newSet = selected
        ? new Set<string>(extractedTasks.map((task) => task.taskInterfaceID))
        : new Set<string>();
      setSelectedTasks(newSet);
      StorageUtils.saveSelectedTasks(newSet);
    },
    [extractedTasks]
  );

  const addCustomTask = useCallback((task: TaskInterface) => {
    setExtractedTasksState((prev) => {
      const exists = prev.some(
        (t) => t.taskInterfaceID === task.taskInterfaceID
      );
      const newTasks = exists
        ? prev.map((t) =>
            t.taskInterfaceID === task.taskInterfaceID ? task : t
          )
        : [...prev, task];
      StorageUtils.saveExtractedTasks(newTasks);
      return newTasks;
    });
  }, []);

  const removeTask = useCallback((taskId: string) => {
    setExtractedTasksState((prev) => {
      const newTasks = prev.filter((t) => t.taskInterfaceID !== taskId);
      StorageUtils.saveExtractedTasks(newTasks);
      return newTasks;
    });
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      next.delete(taskId);
      StorageUtils.saveSelectedTasks(next);
      return next;
    });
  }, []);

  const clearExtractedTasks = useCallback(() => {
    setExtractedTasksState([]);
    setSelectedTasks(new Set());
    setExtractionError(null);
    StorageUtils.saveExtractedTasks([]);
    StorageUtils.saveSelectedTasks(new Set());
    StorageUtils.saveExtractionError(null);
  }, []);

  // ==================== HELPERS ====================
  const getSelectedTasksList = useCallback((): TaskInterface[] => {
    return extractedTasks.filter((task) =>
      selectedTasks.has(task.taskInterfaceID)
    );
  }, [extractedTasks, selectedTasks]);

  // ==================== CONTEXT VALUE ====================
  const value: TaskContextValue = {
    extractedTasks,
    selectedTasks,
    isExtracting,
    isGenerating,
    extractionError,
    setExtractedTasks,
    selectTask,
    selectAllTasks,
    addCustomTask,
    removeTask,
    clearExtractedTasks,
    setIsExtracting,
    setIsGenerating,
    setExtractionError,
    getSelectedTasksList,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

// ==================== HOOK ====================
export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTasks must be used within TaskContextProvider");
  }
  return context;
};
